import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Button } from 'primereact/button'
import { Calendar, CalendarDateTemplateEvent } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { InputNumber } from 'primereact/inputnumber'
import { classNames } from 'primereact/utils'

import { calculateBreakpointWidth } from '../../../../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../../../ui-config'
import { FormFieldName } from './AddSplitItem.container'
import { ReservedDateTemplate } from './ReservedDateTemplate'

interface Props {
  deliveryForecastId: string
  visible: boolean
  setVisible: (params: boolean) => void
  formik: FormikProps<any>
  isFormFieldValid: (name: FormFieldName) => boolean
  getFormErrorMessage: (name: FormFieldName) => false | JSX.Element
  handleClose: () => void
  isRequestRunning: boolean
  disabledDates: Date[]
}

export const AddSplitItemView: React.FC<Props> = ({
  deliveryForecastId,
  visible,
  setVisible,
  formik,
  isFormFieldValid,
  getFormErrorMessage,
  handleClose,
  isRequestRunning,
  disabledDates,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()

  // ************************************************************
  // components
  // ************************************************************
  // confirmationDeliveryDate
  const confirmationDeliveryDateLabel = (
    <label
      htmlFor="confirmationDeliveryDate"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationDeliveryDate'),
      })}
    ></label>
  )

  const confirmationDeliveryDateComponent = (
    <Calendar
      id="confirmationDeliveryDate"
      value={formik.values.confirmationDeliveryDate}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationDeliveryDate'),
      })}
      disabledDates={disabledDates}
      showButtonBar
      showIcon
      minDate={new Date()}
      dateTemplate={(date: CalendarDateTemplateEvent) => (
        <ReservedDateTemplate date={date} reservedDates={disabledDates} />
      )}
      locale={language}
    />
  )

  const confirmationDeliveryDateErrorMsg = (
    <div>{getFormErrorMessage('confirmationDeliveryDate')}</div>
  )

  const confirmationDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.addSplitItem}
      component={confirmationDeliveryDateComponent}
      label={confirmationDeliveryDateLabel}
      errorMsg={confirmationDeliveryDateErrorMsg}
    />
  )

  // confirmationDeliveryQuantity
  const confirmationDeliveryQuantityLabel = (
    <label
      htmlFor="confirmationDeliveryQuantity"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationDeliveryQuantity'),
      })}
    ></label>
  )

  const confirmationDeliveryQuantityComponent = (
    <InputNumber
      id="confirmationDeliveryQuantity"
      value={formik.values.confirmationDeliveryQuantity}
      onValueChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationDeliveryQuantity'),
      })}
      showButtons
      locale={language}
      min={1}
    />
  )

  const confirmationDeliveryQuantityErrorMsg = (
    <div>{getFormErrorMessage('confirmationDeliveryQuantity')}</div>
  )

  const confirmationDeliveryQuantityWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.addSplitItem}
      component={confirmationDeliveryQuantityComponent}
      label={confirmationDeliveryQuantityLabel}
      errorMsg={confirmationDeliveryQuantityErrorMsg}
    />
  )

  // ************************************************************
  // dialog components
  // ************************************************************
  // header
  const header = (
    <>
      <div className="flex align-content-center flex-wrap">
        <span className="mr-3">
          {t(
            'deliveryForecast.deliveryScheduleDetails.splits.table.add.dialog.title',
          )}
        </span>
      </div>
    </>
  )

  // footer
  const footer = (
    <>
      <div className="flex flex-wrap justify-content-between pt-3">
        <Button
          className="p-button-text ml-auto w-full md:w-auto mb-1 md:mb-0"
          label={t('common.buttons.cancel')}
          icon="pi pi-times"
          onClick={handleClose}
          type="button"
        />
        <Button
          label={t('common.buttons.add')}
          className="w-full md:w-auto"
          icon={'pi pi-check'}
          type="submit"
          onClick={formik.submitForm}
          loading={isRequestRunning}
        />
      </div>
    </>
  )

  return (
    <>
      <Button
        label={t('common.buttons.add')}
        icon="pi pi-plus"
        className="p-button-outlined mb-2 mr-2"
        onClick={() => setVisible(true)}
        visible={config.deliveryForecast.addSplitItem.enabled !== false}
      />

      <Dialog
        header={header}
        visible={visible}
        style={{ width: '55rem' }}
        breakpoints={{ [calculateBreakpointWidth(55)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        <div className="formgrid grid">
          <div className="col-12 md:col-6">
            {confirmationDeliveryDateWrapper}
          </div>
          <div className="col-12 md:col-6">
            {confirmationDeliveryQuantityWrapper}
          </div>
        </div>
      </Dialog>
    </>
  )
}
